import { useEffect, useSyncExternalStore } from 'react';
import { create } from 'zustand';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'sixone8.theme';

function readStored(): ThemePreference {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    // Storage can be blocked; the default still renders correctly.
  }
  return 'dark';
}

interface ThemeState {
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
}

/**
 * The preference is the account's `theme_preference` once signed in (the session syncs it here);
 * localStorage only covers the moments before the profile loads, so sign-in never flashes.
 */
export const useThemeStore = create<ThemeState>((set) => ({
  preference: readStored(),
  setPreference: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Non-fatal.
    }
    set({ preference });
  },
}));

const media = () => window.matchMedia('(prefers-color-scheme: light)');

function subscribeSystem(cb: () => void) {
  const m = media();
  m.addEventListener('change', cb);
  return () => m.removeEventListener('change', cb);
}

export function useResolvedTheme(): ResolvedTheme {
  const preference = useThemeStore((s) => s.preference);
  const systemLight = useSyncExternalStore(subscribeSystem, () => media().matches);
  if (preference === 'system') return systemLight ? 'light' : 'dark';
  return preference;
}

/** Mount once at the root: keeps `<html data-theme>` in step with the resolved theme. */
export function useApplyTheme() {
  const theme = useResolvedTheme();
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  return theme;
}

/**
 * Reads the live value of a CSS custom property, for libraries that need concrete colours (both
 * chart libraries). Call inside an effect keyed on the resolved theme so charts recolour on switch.
 */
export function cssVar(name: string, el: Element = document.documentElement): string {
  return getComputedStyle(el).getPropertyValue(name).trim();
}
